/**
 * Lightweight toast system — no external deps.
 * Usage:
 *   import { toast } from '@/components/ui/toast';
 *   toast.error('Network error: ' + err.message);
 *   toast.success('Saved!');
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { CheckCircle2, AlertTriangle, Info, X, WifiOff } from 'lucide-react';

/* ── Types ────────────────────────────────────────────── */
export type ToastKind = 'success' | 'error' | 'info' | 'warn';

export interface Toast {
  id: number;
  kind: ToastKind;
  title: string;
  message?: string;
}

/* ── Context ──────────────────────────────────────────── */
interface ToastContextValue {
  push: (t: Omit<Toast, 'id'>) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/* ── Singleton bridge (for use outside React tree) ────── */
let _push: ToastContextValue['push'] | null = null;

export const toast = {
  success: (title: string, message?: string) => _push?.({ kind: 'success', title, message }),
  error:   (title: string, message?: string) => _push?.({ kind: 'error',   title, message }),
  warn:    (title: string, message?: string) => _push?.({ kind: 'warn',    title, message }),
  info:    (title: string, message?: string) => _push?.({ kind: 'info',    title, message }),
  /** Automatically formats network/fetch errors */
  networkError: (err: unknown, context = 'request') => {
    const msg = err instanceof Error ? err.message : String(err);
    const isOffline = msg.toLowerCase().includes('failed to fetch') || msg.toLowerCase().includes('network');
    _push?.({
      kind: 'error',
      title: isOffline ? 'Could not reach the server' : `${context} failed`,
      message: isOffline
        ? 'Check that the API is running and try again.'
        : msg,
    });
  },
};

/* ── Provider ─────────────────────────────────────────── */
let _nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((t: Omit<Toast, 'id'>) => {
    const id = ++_nextId;
    setToasts(prev => [...prev, { ...t, id }]);
    setTimeout(() => setToasts(prev => prev.filter(x => x.id !== id)), 4500);
  }, []);

  _push = push;

  const dismiss = (id: number) => setToasts(prev => prev.filter(x => x.id !== id));

  return (
    <ToastContext.Provider value={{ push }}>
      {children}
      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

/* ── Hook ─────────────────────────────────────────────── */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be inside <ToastProvider>');
  return ctx;
}

/* ── Visual stack ─────────────────────────────────────── */
const ICONS: Record<ToastKind, ReactNode> = {
  success: <CheckCircle2 size={16} />,
  error:   <WifiOff size={16} />,
  warn:    <AlertTriangle size={16} />,
  info:    <Info size={16} />,
};

const COLORS: Record<ToastKind, { border: string; icon: string; bar: string }> = {
  success: { border: '#10b98140', icon: '#34d399', bar: '#10b981' },
  error:   { border: '#ef444440', icon: '#f87171', bar: '#ef4444' },
  warn:    { border: '#f59e0b40', icon: '#fbbf24', bar: '#f59e0b' },
  info:    { border: '#6366f140', icon: '#818cf8', bar: '#6366f1' },
};

function ToastStack({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: number) => void }) {
  if (toasts.length === 0) return null;
  return (
    <div
      aria-live="polite"
      aria-label="Notifications"
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        maxWidth: 360,
        width: '100%',
        pointerEvents: 'none',
      }}
    >
      {toasts.map(t => {
        const c = COLORS[t.kind];
        return (
          <div
            key={t.id}
            role="alert"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              padding: '13px 14px',
              background: '#181c2a',
              border: `1px solid ${c.border}`,
              borderLeft: `3px solid ${c.bar}`,
              borderRadius: 10,
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              animation: 'toastIn 0.22s cubic-bezier(0.34,1.56,0.64,1)',
              pointerEvents: 'auto',
            }}
          >
            {/* Icon */}
            <span style={{ color: c.icon, flexShrink: 0, paddingTop: 1 }}>{ICONS[t.kind]}</span>

            {/* Text */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#e2e8f0', lineHeight: 1.4 }}>
                {t.title}
              </p>
              {t.message && (
                <p style={{ margin: '3px 0 0', fontSize: 12, color: '#64748b', lineHeight: 1.4 }}>
                  {t.message}
                </p>
              )}
            </div>

            {/* Dismiss */}
            <button
              onClick={() => onDismiss(t.id)}
              aria-label="Dismiss notification"
              style={{
                flexShrink: 0, background: 'none', border: 'none',
                color: '#475569', cursor: 'pointer', padding: 2,
                borderRadius: 4, lineHeight: 0,
              }}
            >
              <X size={14} />
            </button>
          </div>
        );
      })}
      <style>{`
        @keyframes toastIn {
          from { opacity: 0; transform: translateY(12px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
      `}</style>
    </div>
  );
}
